/**
 * Tiered report generation — the Phase-3 cost lever from
 * `docs/architecture/sed-report-cost-strategy.md` (§6 TIERED GENERATION MODEL,
 * §7 Phase 3).
 *
 * Historical tier values are threaded through `runTypedReportGeneration` →
 * `generateReportFromSed`, but generation is now investment-grade only:
 *
 *   - 'investment-grade' (the DEFAULT): the finalization / deliverable path.
 *     It IS today's path, byte-for-byte — ≤4 review passes on Pro, the full
 *     qualitative LLM-judge audits, the full final sanity check. Resolving a
 *     tier to its config MUST leave every knob unset (undefined) so each
 *     downstream consumer keeps its existing default. There is NO behaviour
 *     change for the default.
 *
 *   - 'draft' is retained in the serialized type only for backward
 *     compatibility. Stale clients, queued jobs, and saved site overrides may
 *     still send it, so the resolver canonicalizes it to 'investment-grade'.
 *
 * IMPORTANT: this module owns the per-tier KNOBS, not the gates. The gates
 * (fact-check, citation density, precedence) live in their own modules and run
 * unconditionally — this config can only loosen the *polish* (model tier +
 * iteration count + qualitative judges), never the correctness floor.
 */
export type GenerationTier = 'draft' | 'investment-grade';

/** The default tier. Equals today's full-quality path. */
export const DEFAULT_GENERATION_TIER: GenerationTier = 'investment-grade';

/** @deprecated Retained for compatibility with older imports; draft generation is disabled. */
export const DRAFT_MAX_REVIEW_ITERATIONS = 2;

/**
 * Per-tier knobs the quality pipeline + post-gen audit gates consume.
 *
 * Every field is OPTIONAL. For 'investment-grade' EVERY field is `undefined`
 * so each consumer falls through to its current default — that is the
 * mechanism by which the default path is byte-comparable to today.
 */
export interface TierGenerationConfig {
  /** Hard cap on review-loop iterations. `undefined` ⇒ pipeline default (4). */
  maxReviewIterations?: number;
  /**
   * Model id override for the review-loop pass. The only supported tier leaves
   * this undefined so the pipeline resolves Pro / best available.
   */
  reviewModelOverride?: string;
  /**
   * Model id override for the final sanity check. The only supported tier
   * leaves this undefined so the pipeline resolves Pro.
   */
  sanityModelOverride?: string;
  /**
   * Whether the qualitative LLM-judge audits (R-63 section-drift judge) run.
   * The only supported tier leaves this undefined, preserving
   * AUDIT_CONFIG.ENABLE_LLM_JUDGE and today's full-quality behaviour.
   */
  useLlmJudge?: boolean;
}

/**
 * Resolve a (possibly stale / legacy) tier value to the only supported tier.
 *
 * `draft` remains accepted at the type boundary because old queued jobs and
 * persisted overrides can outlive a frontend deployment. Canonicalizing here
 * prevents those payloads from downgrading a newly generated report or
 * stamping it as a draft.
 */
export function resolveGenerationTier(_tier: GenerationTier | undefined | null): GenerationTier {
  return 'investment-grade';
}

/** New generations are never draft-tier, including from stale draft inputs. */
export function isDraftTier(tier: GenerationTier | undefined | null): boolean {
  return resolveGenerationTier(tier) === 'draft';
}

/**
 * Map a tier to its generation knobs.
 *
 * Every accepted input resolves to the investment-grade no-op configuration.
 * Downstream consumers therefore retain their full-quality defaults.
 */
export function tierGenerationConfig(_tier: GenerationTier | undefined | null): TierGenerationConfig {
  return {};
}
